/**
 * Locale type: any ISO-like language code.
 * Builder allows the user to add/remove locales at runtime, so we can't restrict it.
 */
export type Locale = string

/** Translatable string – missing locales fall back to default locale at render time. */
export type Translatable = Record<string, string>

/** Nuxt UI primary palettes — same names as `app.config.ts` → `ui.colors.primary`. */
export const PRIMARY_THEME_COLORS = [
  { id: 'red', label: 'قرمز', swatchClass: 'bg-red-500' },
  { id: 'orange', label: 'نارنجی', swatchClass: 'bg-orange-500' },
  { id: 'amber', label: 'کهربایی', swatchClass: 'bg-amber-500' },
  { id: 'yellow', label: 'زرد', swatchClass: 'bg-yellow-500' },
  { id: 'lime', label: 'لیمویی', swatchClass: 'bg-lime-500' },
  { id: 'green', label: 'سبز', swatchClass: 'bg-green-500' },
  { id: 'emerald', label: 'زمردی', swatchClass: 'bg-emerald-500' },
  { id: 'teal', label: 'فیروزه', swatchClass: 'bg-teal-500' },
  { id: 'cyan', label: 'سیان', swatchClass: 'bg-cyan-500' },
  { id: 'sky', label: 'آسمانی', swatchClass: 'bg-sky-500' },
  { id: 'blue', label: 'آبی', swatchClass: 'bg-blue-500' },
  { id: 'indigo', label: 'ایندیگو', swatchClass: 'bg-indigo-500' },
  { id: 'violet', label: 'بنفش', swatchClass: 'bg-violet-500' },
  { id: 'purple', label: 'پرپل', swatchClass: 'bg-purple-500' },
  { id: 'fuchsia', label: 'فوشیا', swatchClass: 'bg-fuchsia-500' },
  { id: 'pink', label: 'صورتی', swatchClass: 'bg-pink-500' },
  { id: 'rose', label: 'رز', swatchClass: 'bg-rose-500' },
] as const

/** Nuxt UI neutral palettes — same names as `app.config.ts` → `ui.colors.neutral`. */
export const NEUTRAL_THEME_COLORS = [
  { id: 'slate', label: 'اسلیت', swatchClass: 'bg-slate-500' },
  { id: 'gray', label: 'خاکستری', swatchClass: 'bg-gray-500' },
  { id: 'neutral', label: 'خنثی', swatchClass: 'bg-neutral-500' },
] as const

export type PrimaryThemeColor = (typeof PRIMARY_THEME_COLORS)[number]['id']
export type NeutralThemeColor = (typeof NEUTRAL_THEME_COLORS)[number]['id']

export const DEFAULT_THEME_PRIMARY: PrimaryThemeColor = 'blue'
export const DEFAULT_THEME_NEUTRAL: NeutralThemeColor = 'slate'

/** @deprecated Use PRIMARY_THEME_COLORS / NEUTRAL_THEME_COLORS. Kept for hex→palette migration. */
export const THEME_PRESETS = [
  { id: 'blue', color: '#2563eb', label: 'آبی' },
  { id: 'green', color: '#16a34a', label: 'سبز' },
  { id: 'red', color: '#dc2626', label: 'قرمز' },
  { id: 'orange', color: '#ea580c', label: 'نارنجی' },
  { id: 'purple', color: '#9333ea', label: 'بنفش' },
  { id: 'pink', color: '#db2777', label: 'صورتی' },
  { id: 'teal', color: '#0d9488', label: 'فیروزه' },
  { id: 'amber', color: '#d97706', label: 'کهربایی' },
  { id: 'slate', color: '#475569', label: 'خاکستری' },
] as const

export function isPrimaryThemeColor(value: string): value is PrimaryThemeColor {
  return PRIMARY_THEME_COLORS.some((c) => c.id === value)
}

export function normalizeThemePrimary(value: string | undefined | null): PrimaryThemeColor {
  if (value && isPrimaryThemeColor(value)) return value
  return DEFAULT_THEME_PRIMARY
}

export function isNeutralThemeColor(value: string): value is NeutralThemeColor {
  return NEUTRAL_THEME_COLORS.some((c) => c.id === value)
}

export function normalizeThemeNeutral(value: string | undefined | null): NeutralThemeColor {
  if (value && isNeutralThemeColor(value)) return value
  return DEFAULT_THEME_NEUTRAL
}

/** Map legacy hex accents to the closest Nuxt UI primary id. */
export function primaryFromLegacyHex(hex: string | undefined | null): PrimaryThemeColor {
  if (!hex) return DEFAULT_THEME_PRIMARY
  const normalized = hex.trim().toLowerCase()
  const hit = THEME_PRESETS.find((p) => p.color.toLowerCase() === normalized)
  if (hit && isPrimaryThemeColor(hit.id)) return hit.id
  return DEFAULT_THEME_PRIMARY
}

/** Site font presets — bundled stacks the builder can assign. */
export const FONT_PRESETS = [
  {
    id: 'iran-yekan',
    family: 'IranYekan',
    label: { en: 'Iran Yekan', fa: 'ایران‌یکان' },
    stack: "'IranYekan', sans-serif",
  },
] as const

export const DEFAULT_FONT_FAMILY = FONT_PRESETS[0].family

export function fontLabel(family: string, locale: Locale): string {
  const preset = FONT_PRESETS.find((f) => f.family === family)
  if (!preset) return family
  return preset.label[locale] ?? preset.label.en ?? family
}

export function fontStack(family: string): string {
  const preset = FONT_PRESETS.find((f) => f.family === family)
  if (preset) return preset.stack
  if (family.includes(',')) return family
  return `'${family}', sans-serif`
}

/** Catalog of languages the builder knows how to seed. */
export type LocaleDescriptor = {
  code: string
  name: string
  nativeName: string
  dir: 'ltr' | 'rtl'
  flag: string
}

export const LOCALE_CATALOG: LocaleDescriptor[] = [
  { code: 'en', name: 'English', nativeName: 'English', dir: 'ltr', flag: '🇬🇧' },
  { code: 'fa', name: 'Persian', nativeName: 'فارسی', dir: 'rtl', flag: '🇮🇷' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', dir: 'rtl', flag: '🇸🇦' },
  { code: 'fr', name: 'French', nativeName: 'Français', dir: 'ltr', flag: '🇫🇷' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', dir: 'ltr', flag: '🇩🇪' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', dir: 'ltr', flag: '🇪🇸' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', dir: 'ltr', flag: '🇮🇹' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', dir: 'ltr', flag: '🇹🇷' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', dir: 'ltr', flag: '🇷🇺' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', dir: 'ltr', flag: '🇨🇳' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', dir: 'ltr', flag: '🇯🇵' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', dir: 'ltr', flag: '🇮🇳' },
]

export function getLocaleInfo(code: string): LocaleDescriptor {
  return (
    LOCALE_CATALOG.find((l) => l.code === code) ?? {
      code,
      name: code.toUpperCase(),
      nativeName: code.toUpperCase(),
      dir: 'ltr',
      flag: '🌐',
    }
  )
}

export function isRtlLocale(code: string): boolean {
  return getLocaleInfo(code).dir === 'rtl'
}

export const BLOCK_META: Record<
  string,
  {
    label: Record<string, string>
    description: Record<string, string>
    icon: string
    /** Where the block can live: site-level (in layout) or per-page. */
    scope: 'layout' | 'page'
    /** Any-of: block is available if the site has at least one of these modules. */
    requiredModules?: string[]
    /** When set, the block only appears for these site ids. */
    tenantAllowlist?: string[]
  }
> = {
  BlockHeader: {
    label: { en: 'Header', fa: 'هدر' },
    description: { en: 'Logo and navigation', fa: 'لوگو و منو' },
    icon: 'i-heroicons-bars-3-bottom-left',
    scope: 'layout',
  },
  BlockFooter: {
    label: { en: 'Footer', fa: 'فوتر' },
    description: { en: 'Bottom links and copyright', fa: 'لینک‌های پایینی و کپی‌رایت' },
    icon: 'i-heroicons-bars-3-bottom-right',
    scope: 'layout',
  },
  BlockBanner: {
    label: { en: 'Top banner', fa: 'بنر اعلان' },
    description: { en: 'Announcement banner', fa: 'بنر اعلان بالای صفحه' },
    icon: 'i-heroicons-megaphone',
    scope: 'layout',
  },
  BlockLandingHero: {
    label: { en: 'Hero banner', fa: 'بنر اصلی' },
    description: { en: 'Main headline and call-to-action', fa: 'عنوان و دکمه اصلی صفحه' },
    icon: 'i-heroicons-sparkles',
    scope: 'page',
  },
  BlockHero: {
    label: { en: 'Simple hero', fa: 'هیرو ساده' },
    description: { en: 'Compact hero section', fa: 'بخش معرفی فشرده' },
    icon: 'i-heroicons-photo',
    scope: 'page',
  },
  BlockFeatures: {
    label: { en: 'Features', fa: 'ویژگی‌ها' },
    description: { en: 'Highlight key benefits', fa: 'نمایش مزایای اصلی' },
    icon: 'i-heroicons-squares-2x2',
    scope: 'page',
  },
  BlockProducts: {
    label: { en: 'Services / products', fa: 'خدمات / محصولات' },
    description: { en: 'Cards with pricing', fa: 'کارت‌های قیمت‌دار' },
    icon: 'i-heroicons-shopping-bag',
    scope: 'page',
    requiredModules: ['shop', 'pricing'],
  },
  BlockBlogList: {
    label: { en: 'Blog list', fa: 'لیست بلاگ' },
    description: { en: 'Latest posts grid', fa: 'گرید پست‌های اخیر' },
    icon: 'i-heroicons-newspaper',
    scope: 'page',
    requiredModules: ['blog'],
  },
  BlockFaq: {
    label: { en: 'FAQ', fa: 'سؤالات متداول' },
    description: { en: 'Questions and answers', fa: 'پرسش و پاسخ' },
    icon: 'i-heroicons-question-mark-circle',
    scope: 'page',
    requiredModules: ['faq'],
  },
  BlockServices: {
    label: { en: 'Services carousel', fa: 'اسلایدر خدمات' },
    description: { en: 'Swiper of service cards', fa: 'کارت‌های خدمات در اسلایدر' },
    icon: 'i-heroicons-rectangle-stack',
    scope: 'page',
  },
  BlockServiceGroups: {
    label: { en: 'Service groups', fa: 'گروه خدمت' },
    description: { en: 'Published service groups', fa: 'نمایش گروه‌های خدمت منتشرشده' },
    icon: 'i-lucide-layers',
    scope: 'page',
  },
  BlockAppointment: {
    label: { en: 'Appointment card', fa: 'کارت نوبت‌دهی' },
    description: { en: 'Live cards for every bookable service', fa: 'کارت زنده همه خدمات نوبت‌دهی' },
    icon: 'i-heroicons-calendar-days',
    scope: 'page',
    requiredModules: ['onlineBooking'],
  },
  BlockBookingWizard: {
    label: { en: 'Booking wizard', fa: 'فرم رزرو نوبت' },
    description: { en: 'Multi-step online booking form', fa: 'ویزارد چندمرحله‌ای رزرو نوبت' },
    icon: 'i-heroicons-calendar-days',
    scope: 'page',
    requiredModules: ['onlineBooking'],
  },
  BlockTestimonials: {
    label: { en: 'Testimonials', fa: 'نظرات بیماران' },
    description: { en: 'Patient reviews slider', fa: 'اسلایدر نظرات بیماران' },
    icon: 'i-heroicons-chat-bubble-left-right',
    scope: 'page',
    requiredModules: ['testimonials'],
  },
  BlockDoctorIntro: {
    label: { en: 'Doctor intro', fa: 'معرفی پزشک' },
    description: { en: 'Photo, bio and CTAs', fa: 'عکس، معرفی و دکمه‌ها' },
    icon: 'i-heroicons-user-circle',
    scope: 'page',
  },
  BlockHighlights: {
    label: { en: 'Highlights', fa: 'هایلایت‌ها' },
    description: { en: 'Icon highlight grid', fa: 'گرید آیکون‌های برجسته' },
    icon: 'i-heroicons-sparkles',
    scope: 'page',
  },
  BlockCredentials: {
    label: { en: 'Credentials', fa: 'مدارک' },
    description: { en: 'Degrees and certifications', fa: 'مدارک و گواهینامه‌ها' },
    icon: 'i-heroicons-academic-cap',
    scope: 'page',
  },
  BlockReasons: {
    label: { en: 'Why choose us', fa: 'چرا ما' },
    description: { en: 'Numbered reason cards', fa: 'کارت‌های دلایل شماره‌دار' },
    icon: 'i-heroicons-hand-thumb-up',
    scope: 'page',
  },
  BlockSpecialties: {
    label: { en: 'Specialties', fa: 'تخصص‌ها' },
    description: { en: 'Specialty cards grid', fa: 'گرید کارت‌های تخصص' },
    icon: 'i-heroicons-heart',
    scope: 'page',
  },
  BlockCtaBanner: {
    label: { en: 'CTA banner', fa: 'بنر فراخوان' },
    description: { en: 'Bottom call-to-action', fa: 'فراخوان به اقدام پایین صفحه' },
    icon: 'i-heroicons-megaphone',
    scope: 'page',
  },
  BlockContact: {
    label: { en: 'Contact info', fa: 'اطلاعات تماس' },
    description: { en: 'Phone and address', fa: 'تلفن و آدرس مطب' },
    icon: 'i-heroicons-map-pin',
    scope: 'page',
  },
  BlockTeam: {
    label: { en: 'Medical team', fa: 'تیم پزشکی' },
    description: { en: 'Doctors and staff grid', fa: 'گرید پزشکان و کارکنان' },
    icon: 'i-lucide-users',
    scope: 'page',
    requiredModules: ['team'],
  },
  BlockInsurance: {
    label: { en: 'Insurance partners', fa: 'بیمه‌های طرف قرارداد' },
    description: { en: 'Accepted insurance logos', fa: 'لوگوهای بیمه‌های پذیرفته‌شده' },
    icon: 'i-lucide-shield-check',
    scope: 'page',
    requiredModules: ['insurance'],
  },
  BlockGallery: {
    label: { en: 'Gallery', fa: 'گالری' },
    description: { en: 'Photo gallery grid', fa: 'گرید تصاویر گالری' },
    icon: 'i-lucide-images',
    scope: 'page',
    requiredModules: ['gallery'],
  },
  BlockSpinWheel: {
    label: { en: 'Spin wheel', fa: 'گردونه شانس' },
    description: { en: 'Prize wheel with phone gate', fa: 'گردونه جایزه با ورود شماره' },
    icon: 'i-lucide-circle-dot',
    scope: 'page',
    requiredModules: ['clinicCampaign'],
  },
  BlockFlashSale: {
    label: { en: 'Flash sale', fa: 'فروش ویژه' },
    description: { en: 'Timed discount with limited slots', fa: 'تخفیف زمان‌دار با ظرفیت محدود' },
    icon: 'i-lucide-zap',
    scope: 'page',
    requiredModules: ['clinicCampaign'],
  },
  // Deviation: BLOCK_META has a single scope; the banner is also listed in LAYOUT_BLOCK_ORDER so it can live in layout or on a page.
  BlockCampaignBanner: {
    label: { en: 'Campaign banner', fa: 'بنر کمپین' },
    description: { en: 'Top-bar, popup or sticky campaign offer', fa: 'نوار بالا، پاپ‌آپ یا بنر چسبان کمپین' },
    icon: 'i-lucide-megaphone',
    scope: 'page',
    requiredModules: ['clinicCampaign'],
  },
  BlockNewsletterCapture: {
    label: { en: 'Newsletter capture', fa: 'عضویت خبرنامه' },
    description: { en: 'Phone capture for campaign SMS', fa: 'ثبت شماره برای پیامک کمپین' },
    icon: 'i-lucide-mail',
    scope: 'page',
    requiredModules: ['clinicCampaign'],
  },
  BlockLeadMagnet: {
    label: { en: 'Lead magnet', fa: 'محتوای رایگان' },
    description: { en: 'Download gated by phone', fa: 'دانلود فایل با ثبت موبایل' },
    icon: 'i-lucide-file-down',
    scope: 'page',
    requiredModules: ['leadGen'],
  },
  BlockChecklist: {
    label: { en: 'Checklist', fa: 'چک‌لیست' },
    description: { en: 'Checklist behind a phone gate', fa: 'چک‌لیست پشت فرم موبایل' },
    icon: 'i-lucide-list-todo',
    scope: 'page',
    requiredModules: ['leadGen'],
  },
  BlockLeadCalculator: {
    label: { en: 'Health calculator', fa: 'ماشین‌حساب سلامت' },
    description: { en: 'Multi-step scored calculator', fa: 'فرم چندمرحله‌ای با نتیجه شخصی' },
    icon: 'i-lucide-calculator',
    scope: 'page',
    requiredModules: ['leadGen'],
  },
  BlockLeadQuiz: {
    label: { en: 'Medical quiz', fa: 'آزمون پزشکی' },
    description: { en: 'Quiz with a shareable result', fa: 'کوئیز با نتیجه قابل اشتراک' },
    icon: 'i-lucide-list-checks',
    scope: 'page',
    requiredModules: ['leadGen'],
  },
  BlockConsultationForm: {
    label: { en: 'Consultation form', fa: 'فرم مشاوره' },
    description: { en: 'Short consult request', fa: 'درخواست مشاوره اولیه' },
    icon: 'i-lucide-stethoscope',
    scope: 'page',
    requiredModules: ['leadGen'],
  },
  BlockLeadSurvey: {
    label: { en: 'Survey', fa: 'نظرسنجی' },
    description: { en: 'Short survey with optional reward', fa: 'نظرسنجی کوتاه با جایزه' },
    icon: 'i-lucide-clipboard-list',
    scope: 'page',
    requiredModules: ['leadGen'],
  },
  BlockPersonalizedGuide: {
    label: { en: 'Personalized guide', fa: 'راهنمای شخصی' },
    description: { en: 'Rule-based content by answers', fa: 'محتوای قانون‌محور بر اساس پاسخ' },
    icon: 'i-lucide-book-open',
    scope: 'page',
    requiredModules: ['leadGen'],
  },
  BlockEventRegistration: {
    label: { en: 'Event registration', fa: 'ثبت‌نام رویداد' },
    description: { en: 'Webinar or workshop signup', fa: 'ثبت‌نام وبینار یا کارگاه' },
    icon: 'i-lucide-presentation',
    scope: 'page',
    requiredModules: ['leadGen'],
  },
  BlockWaitlistForm: {
    label: { en: 'Waitlist', fa: 'لیست انتظار' },
    description: { en: 'Pre-register for a new service', fa: 'پیش‌ثبت‌نام خدمت جدید' },
    icon: 'i-lucide-hourglass',
    scope: 'page',
    requiredModules: ['leadGen'],
  },
  BlockSmartContactForm: {
    label: { en: 'Smart contact form', fa: 'فرم هوشمند تماس' },
    description: { en: 'Scored contact questions', fa: 'فرم تماس با امتیازدهی' },
    icon: 'i-lucide-contact',
    scope: 'page',
    requiredModules: ['leadGen'],
  },
  BlockLeadChatWidget: {
    label: { en: 'Lead chat widget', fa: 'ویجت چت لید' },
    description: { en: 'Floating pre-booking chat', fa: 'چت شناور قبل از نوبت' },
    icon: 'i-lucide-message-circle',
    scope: 'layout',
    requiredModules: ['leadGen'],
  },
}

/** Order in which blocks appear in the "Add block" picker. */
export const PAGE_BLOCK_ORDER: string[] = [
  'BlockBanner',
  'BlockLandingHero',
  'BlockHero',
  'BlockDoctorIntro',
  'BlockFeatures',
  'BlockHighlights',
  'BlockServices',
  'BlockServiceGroups',
  'BlockProducts',
  'BlockAppointment',
  'BlockCredentials',
  'BlockReasons',
  'BlockSpecialties',
  'BlockBlogList',
  'BlockTestimonials',
  'BlockTeam',
  'BlockInsurance',
  'BlockGallery',
  'BlockFaq',
  'BlockCtaBanner',
  'BlockContact',
  'BlockSpinWheel',
  'BlockFlashSale',
  'BlockCampaignBanner',
  'BlockNewsletterCapture',
  'BlockLeadMagnet',
  'BlockChecklist',
  'BlockLeadCalculator',
  'BlockLeadQuiz',
  'BlockConsultationForm',
  'BlockLeadSurvey',
  'BlockPersonalizedGuide',
  'BlockEventRegistration',
  'BlockWaitlistForm',
  'BlockSmartContactForm',
]

/** Header first, footer last; banners sit between them. */
export const LAYOUT_BLOCK_ORDER: string[] = ['BlockHeader', 'BlockBanner', 'BlockCampaignBanner', 'BlockLeadChatWidget', 'BlockFooter']

export const PAGE_META: Record<string, { icon: string; label: string }> = {
  home: { icon: 'i-heroicons-home', label: 'صفحه اصلی' },
  services: { icon: 'i-heroicons-briefcase', label: 'خدمات' },
  booking: { icon: 'i-heroicons-calendar-days', label: 'نوبت‌دهی' },
  about: { icon: 'i-heroicons-information-circle', label: 'درباره ما' },
  blog: { icon: 'i-heroicons-newspaper', label: 'بلاگ' },
  contact: { icon: 'i-heroicons-envelope', label: 'تماس' },
  pricing: { icon: 'i-heroicons-tag', label: 'تعرفه' },
  team: { icon: 'i-lucide-users', label: 'تیم پزشکی' },
  shop: { icon: 'i-lucide-shopping-bag', label: 'فروشگاه' },
}

export function pageIcon(slug: string): string {
  return PAGE_META[slug]?.icon ?? 'i-heroicons-document'
}

/** Fixed sidebar list label — independent of editable page.title. */
export function pageListLabel(key: string): string {
  return PAGE_META[key]?.label ?? key
}

export function blockLabel(component: string, locale: Locale): string {
  const meta = BLOCK_META[component]
  if (!meta) return component
  return meta.label[locale] ?? meta.label.en ?? component
}

/** Returns a fresh default props payload for a given block. */
export function buildBlockDefaults(component: string): Record<string, any> {
  switch (component) {
    case 'BlockHeader':
      return {
        logoText: { en: 'Site', fa: 'سایت' },
        logoImage: '',
        menuItems: [
          { label: { en: 'Home', fa: 'خانه' }, to: '/' },
          { label: { en: 'About', fa: 'درباره ما' }, to: '/about' },
        ],
      }

    case 'BlockFooter':
      return {
        variant: 'simple' as const,
        phone: {
          en: 'Clinic phone: 09372587464',
          fa: 'تلفن مطب: 09372587464',
        },
        address: {
          en: 'Isfahan, Shamsabadi St., Pars Alley (next to Isa ibn Maryam Hospital), Maryam Physicians Building, Block 2',
          fa: 'اصفهان، خیابان شمس‌آبادی، کوچه پارس (کوچه جنب بیمارستان عیسی بن مریم)، ساختمان پزشکان مریم، بلوک ۲',
        },
        creatorLabel: {
          en: 'Made by AradCore',
          fa: 'ساخته شده توسط AradCore',
        },
        creatorUrl: 'https://aradcore.com',
        showEnamad: true,
        enamadCode: '',
        logoText: { en: 'Site', fa: 'سایت' },
        tagline: {
          en: 'A modern site built with the visual builder.',
          fa: 'یک وب‌سایت مدرن ساخته‌شده با بیلدر بصری.',
        },
        columns: [],
        copyright: {
          en: '© 2025 Your Site. All rights reserved.',
          fa: '© ۲۰۲۵ سایت شما. تمامی حقوق محفوظ است.',
        },
        socialLinks: [],
      }

    case 'BlockBanner':
      return {
        icon: 'i-heroicons-megaphone',
        title: {
          en: 'Now offering free consultations — limited time only.',
          fa: 'مشاوره رایگان — فقط برای مدت محدود.',
        },
        ctaLabel: { en: 'Learn more', fa: 'اطلاعات بیشتر' },
        ctaTo: '/services',
        color: 'primary' as const,
      }

    case 'BlockLandingHero':
      return {
        headlineImages: { en: [] as string[], fa: [] as string[] },
        bannerAutoplay: false,
        bannerAutoplayDelay: 5000,
        bannerLoop: false,
        title: { en: 'Welcome to your new site', fa: 'به سایت جدید خوش آمدید' },
        description: {
          en: 'Tell visitors what makes you unique.',
          fa: 'به بازدیدکنندگان بگویید چه چیزی شما را متمایز می‌کند.',
        },
        primaryButton: {
          label: { en: 'Get started', fa: 'شروع کنید' },
          to: '/',
        },
        secondaryLinks: [],
        socialLinks: [],
      }

    case 'BlockHero':
      return {
        title: { en: 'Headline goes here', fa: 'عنوان اصلی اینجا قرار می‌گیرد' },
        subtitle: {
          en: 'Short supporting sentence.',
          fa: 'جمله توضیحی کوتاه.',
        },
        image: '',
        ctaText: { en: 'Discover', fa: 'مشاهده' },
        ctaLink: '#',
      }

    case 'BlockFeatures':
      return {
        title: { en: 'What we offer', fa: 'ما چه ارائه می‌دهیم' },
        features: [
          {
            icon: 'i-heroicons-bolt',
            title: { en: 'Fast', fa: 'سریع' },
            description: { en: 'Optimized for speed.', fa: 'بهینه برای سرعت.' },
          },
          {
            icon: 'i-heroicons-shield-check',
            title: { en: 'Secure', fa: 'امن' },
            description: { en: 'Built with security in mind.', fa: 'با تمرکز بر امنیت ساخته شده.' },
          },
          {
            icon: 'i-heroicons-heart',
            title: { en: 'Loved', fa: 'محبوب' },
            description: { en: 'Trusted by users worldwide.', fa: 'مورد اعتماد کاربران در سراسر جهان.' },
          },
        ],
      }

    case 'BlockProducts':
      return {
        title: { en: 'Our services', fa: 'خدمات و تعرفه‌ها' },
        subtitle: {
          en: 'Pick the plan that fits your needs.',
          fa: 'پلنی را انتخاب کنید که نیاز شماست.',
        },
        layout: 'carousel',
        cardVariant: 'solid',
        items: [
          {
            name: { en: 'Starter visit', fa: 'ویزیت پایه' },
            description: { en: 'Best for individuals.', fa: 'مناسب افراد.' },
            price: '۳۵۰٬۰۰۰ تومان',
            badge: '',
            href: '/booking',
            ctaLabel: { en: 'Get consultation', fa: 'دریافت مشاوره' },
            meta: { en: 'Per visit', fa: 'هر جلسه' },
            features: [
              { en: 'In-person visit', fa: 'ویزیت حضوری' },
              { en: 'Basic follow-up', fa: 'پیگیری پایه' },
              { en: 'Prescription if needed', fa: 'نسخه در صورت نیاز' },
            ],
            image: '',
            highlighted: false,
          },
          {
            name: { en: 'Specialist care', fa: 'مراقبت تخصصی' },
            description: { en: 'Best for ongoing treatment.', fa: 'مناسب درمان مستمر.' },
            price: '۷۵۰٬۰۰۰ تومان',
            badge: { en: 'Popular', fa: 'محبوب' },
            href: '/booking',
            ctaLabel: { en: 'Get consultation', fa: 'دریافت مشاوره' },
            meta: { en: 'Per visit', fa: 'هر جلسه' },
            features: [
              { en: 'Specialist consultation', fa: 'مشاوره تخصصی' },
              { en: 'Priority scheduling', fa: 'نوبت‌دهی اولویت‌دار' },
              { en: 'Treatment plan review', fa: 'بررسی برنامه درمان' },
              { en: 'Online follow-up', fa: 'پیگیری آنلاین' },
            ],
            image: '',
            highlighted: true,
          },
        ],
      }

    case 'BlockBlogList':
      return {
        title: { en: 'From the blog', fa: 'از وبلاگ' },
        subtitle: {
          en: 'News, tips, and stories.',
          fa: 'اخبار، نکته‌ها و داستان‌ها.',
        },
        layout: 'grid' as const,
        source: 'latest',
        limit: 6,
        categorySlug: '',
        showSearch: true,
        ctaLabel: { en: 'Read article', fa: 'مطالعه مقاله' },
        posts: [
          {
            title: { en: 'How we built this', fa: 'چگونه این را ساختیم' },
            description: {
              en: 'A look behind the scenes of our visual builder.',
              fa: 'نگاهی پشت صحنه به سازنده بصری ما.',
            },
            date: new Date().toISOString().slice(0, 10),
            image: '',
            badge: { en: 'Engineering', fa: 'مهندسی' },
            to: '#',
          },
          {
            title: { en: '5 design tips', fa: '۵ نکته طراحی' },
            description: {
              en: 'Improve your landing page in 5 minutes.',
              fa: 'صفحه فرود خود را در ۵ دقیقه بهبود دهید.',
            },
            date: new Date().toISOString().slice(0, 10),
            image: '',
            badge: { en: 'Design', fa: 'طراحی' },
            to: '#',
          },
          {
            title: { en: 'Launch announcement', fa: 'اعلام راه‌اندازی' },
            description: {
              en: 'We are open for customers today.',
              fa: 'از امروز پذیرای مشتریان هستیم.',
            },
            date: new Date().toISOString().slice(0, 10),
            image: '',
            badge: { en: 'News', fa: 'اخبار' },
            to: '#',
          },
        ],
      }

    case 'BlockFaq':
      return {
        title: { en: 'Frequently asked questions', fa: 'پرسش‌های متداول' },
        description: {
          en: 'Everything you wanted to know.',
          fa: 'هر چه می‌خواستید بدانید.',
        },
        categories: [
          {
            title: { en: 'General', fa: 'عمومی' },
            questions: [
              {
                label: { en: 'How do I get started?', fa: 'چگونه شروع کنم؟' },
                content: {
                  en: 'Sign up and follow the onboarding steps.',
                  fa: 'ثبت‌نام کنید و مراحل را دنبال کنید.',
                },
              },
            ],
          },
        ],
      }

    case 'BlockServices':
      return {
        title: { en: 'Our services', fa: 'خدمات تخصصی' },
        subtitle: {
          en: 'Expert care tailored to your needs.',
          fa: 'مراقبت تخصصی متناسب با نیاز شما.',
        },
        layout: 'carousel' as const,
        cardVariant: 'solid' as const,
        source: 'both' as const,
        items: [
          {
            id: 'svc-online-consult',
            title: { en: 'Online consultation', fa: 'مشاوره آنلاین' },
            description: {
              en: 'Talk to a specialist from home.',
              fa: 'از منزل با پزشک متخصص صحبت کنید.',
            },
            price: '۴۵۰٬۰۰۰ تومان',
            icon: 'i-lucide-video',
            image: '',
            features: [
              { en: 'Video visit', fa: 'ویزیت ویدیویی' },
              { en: 'Same-day slots', fa: 'نوبت همان روز' },
              { en: 'Digital prescription', fa: 'نسخه دیجیتال' },
            ],
            ctaLabel: { en: 'Get consultation', fa: 'دریافت مشاوره' },
            ctaLink: '/booking',
          },
          {
            id: 'svc-clinic-visit',
            title: { en: 'In-clinic visit', fa: 'ویزیت حضوری' },
            description: {
              en: 'Visit the clinic in person.',
              fa: 'مراجعه حضوری به مطب.',
            },
            price: '۶۵۰٬۰۰۰ تومان',
            icon: 'i-lucide-stethoscope',
            image: '',
            features: [
              { en: 'Full examination', fa: 'معاینه کامل' },
              { en: 'Treatment plan', fa: 'برنامه درمان' },
              { en: 'Follow-up support', fa: 'پشتیبانی پیگیری' },
            ],
            ctaLabel: { en: 'Get consultation', fa: 'دریافت مشاوره' },
            ctaLink: '/booking',
          },
        ],
      }

    case 'BlockServiceGroups':
      return {
        title: { en: 'Service groups', fa: 'گروه‌های خدمت' },
        subtitle: {
          en: 'Explore our services by group.',
          fa: 'خدمات را بر اساس گروه بررسی کنید.',
        },
      }

    case 'BlockContact':
      return {
        title: { en: 'Contact the doctor', fa: 'ارتباط با دکتر' },
        phoneLabel: { en: 'Clinic phone', fa: 'تلفن مطب' },
        phone: { en: '09372587464', fa: '09372587464' },
        addressLabel: { en: 'Clinic address', fa: 'آدرس مطب' },
        address: {
          en: 'Isfahan, Shamsabadi St., Pars Alley (next to Isa ibn Maryam Hospital), Maryam Physicians Building, Block 2',
          fa: 'اصفهان، خیابان شمس‌آبادی، کوچه پارس (کوچه جنب بیمارستان عیسی بن مریم)، ساختمان پزشکان مریم، بلوک ۲',
        },
      }

    case 'BlockAppointment':
      return {
        title: { en: 'Book an appointment', fa: 'رزرو نوبت' },
        subtitle: {
          en: 'Choose a service and request a time.',
          fa: 'خدمت را انتخاب کنید و نوبت بگیرید.',
        },
        layout: 'grid',
        cardVariant: 'soft',
      }

    case 'BlockBookingWizard':
      return {
        title: { en: 'Book an appointment', fa: 'رزرو نوبت آنلاین' },
        description: {
          en: 'Choose a service, pick a time, and confirm with your mobile number.',
          fa: 'خدمت را انتخاب کنید، زمان مناسب را بزنید و با شماره موبایل تأیید کنید.',
        },
        successTitle: { en: 'Appointment booked', fa: 'نوبت شما ثبت شد' },
        successDescription: {
          en: 'A confirmation SMS will be sent to your phone.',
          fa: 'پیامک تأیید به شماره شما ارسال می‌شود.',
        },
      }

    case 'BlockTestimonials':
      return {
        title: { en: 'Patient reviews', fa: 'نظرات بیماران' },
        items: [
          {
            name: { en: 'Sarah', fa: 'سارا' },
            comment: {
              en: 'Excellent care and attention.',
              fa: 'مراقبت و توجه عالی.',
            },
          },
          {
            name: { en: 'Ali', fa: 'علی' },
            comment: {
              en: 'Professional and friendly staff.',
              fa: 'کادر حرفه‌ای و خوش‌برخورد.',
            },
          },
        ],
      }

    case 'BlockDoctorIntro':
      return {
        title: { en: 'Dr. Example', fa: 'دکتر نمونه' },
        description: { en: 'Specialist physician', fa: 'پزشک متخصص' },
        intro: {
          en: 'Welcome to our practice.\n\nWe provide comprehensive care.',
          fa: 'به مطب ما خوش آمدید.\n\nمراقبت جامع ارائه می‌دهیم.',
        },
        image: '',
        imageAlt: { en: 'Doctor portrait', fa: 'تصویر پزشک' },
        primaryLabel: { en: 'Book appointment', fa: 'رزرو نوبت' },
        primaryLink: '/booking',
        secondaryLabel: { en: 'Our services', fa: 'خدمات ما' },
        secondaryLink: '/services',
      }

    case 'BlockHighlights':
      return {
        title: { en: '', fa: '' },
        items: [
          { icon: 'i-heroicons-academic-cap', label: { en: 'Board certified', fa: 'بورد تخصصی' } },
          { icon: 'i-heroicons-heart', label: { en: 'Patient-first', fa: 'اولویت بیمار' } },
          { icon: 'i-heroicons-clock', label: { en: 'Flexible hours', fa: 'ساعات منعطف' } },
        ],
      }

    case 'BlockCredentials':
      return {
        title: { en: 'Credentials', fa: 'مدارک' },
        items: [
          { text: { en: 'Medical degree, University', fa: 'مدرک پزشکی، دانشگاه' } },
        ],
      }

    case 'BlockReasons':
      return {
        title: { en: 'Why choose us', fa: 'چرا ما را انتخاب کنید' },
        items: [
          {
            title: { en: 'Dedicated care', fa: 'مراقبت متعهدانه' },
            description: { en: 'We put your health first.', fa: 'سلامت شما اولویت ماست.' },
            points: [],
          },
        ],
      }

    case 'BlockSpecialties':
      return {
        title: { en: 'Specialties', fa: 'تخصص‌ها' },
        description: { en: 'Areas of expertise.', fa: 'حوزه‌های تخصصی.' },
        ctaLabel: { en: 'View service', fa: 'مشاهده خدمت' },
        items: [
          {
            title: { en: 'General practice', fa: 'پزشکی عمومی' },
            items: [{ en: 'Check-ups', fa: 'معاینات' }],
            ctaLink: '/services',
          },
        ],
      }

    case 'BlockCtaBanner':
      return {
        title: { en: 'Book appointment', fa: 'رزرو نوبت' },
        subtitle: {
          en: 'We look forward to seeing you.',
          fa: 'منتظر دیدار شما هستیم.',
        },
        primaryLabel: { en: 'Book appointment', fa: 'رزرو نوبت' },
        primaryLink: '/booking',
        secondaryLabel: { en: 'Our services', fa: 'خدمات ما' },
        secondaryLink: '/services',
      }

    case 'BlockTeam':
      return {
        title: { en: 'Our medical team', fa: 'تیم پزشکی ما' },
        members: [
          {
            name: { en: 'Dr. Example', fa: 'دکتر نمونه' },
            role: { en: 'Specialist', fa: 'متخصص' },
            image: '',
            bio: {
              en: 'Board-certified physician with years of experience.',
              fa: 'پزشک بورد تخصصی با سال‌ها تجربه.',
            },
          },
          {
            name: { en: 'Dr. Sample', fa: 'دکتر نمونه ۲' },
            role: { en: 'Consultant', fa: 'مشاور' },
            image: '',
            bio: {
              en: 'Focused on patient-centered care.',
              fa: 'متمرکز بر مراقبت بیمارمحور.',
            },
          },
        ],
      }

    case 'BlockInsurance':
      return {
        title: { en: 'Accepted insurance', fa: 'بیمه‌های طرف قرارداد' },
        items: [
          { name: { en: 'Tamin', fa: 'تأمین اجتماعی' }, logo: '' },
          { name: { en: 'Salamat', fa: 'سلامت' }, logo: '' },
          { name: { en: 'Armed Forces', fa: 'نیروهای مسلح' }, logo: '' },
          { name: { en: 'Supplementary', fa: 'تکمیلی' }, logo: '' },
        ],
      }

    case 'BlockGallery':
      return {
        title: { en: 'Gallery', fa: 'گالری' },
        items: [
          {
            image: '',
            caption: { en: 'Clinic interior', fa: 'فضای داخلی کلینیک' },
          },
          {
            image: '',
            caption: { en: 'Waiting area', fa: 'فضای انتظار' },
          },
          {
            image: '',
            caption: { en: 'Examination room', fa: 'اتاق معاینه' },
          },
        ],
      }

    case 'BlockSpinWheel':
      return {
        title: { en: 'Spin and win', fa: 'بچرخانید و برنده شوید' },
        subtitle: {
          en: 'Enter your phone number to try your luck.',
          fa: 'شماره موبایل را وارد کنید و شانس خود را امتحان کنید.',
        },
        wheelId: '',
      }

    case 'BlockFlashSale':
      return {
        title: { en: 'Limited offer', fa: 'پیشنهاد محدود' },
        subtitle: {
          en: 'A short window of discounted visits.',
          fa: 'فرصت کوتاه برای نوبت با تخفیف.',
        },
        ctaLabel: { en: 'Claim offer', fa: 'دریافت پیشنهاد' },
        saleId: '',
      }

    case 'BlockCampaignBanner':
      return {
        title: { en: 'Book this week and save', fa: 'همین هفته نوبت بگیرید و تخفیف بگیرید' },
        ctaLabel: { en: 'Book now', fa: 'رزرو نوبت' },
        ctaLink: '/booking',
        style: 'top-bar',
      }

    case 'BlockNewsletterCapture':
      return {
        title: { en: 'Clinic offers on SMS', fa: 'پیشنهادهای مطب با پیامک' },
        subtitle: {
          en: 'Leave your number for seasonal campaigns.',
          fa: 'شماره خود را برای کمپین‌های مناسبتی بگذارید.',
        },
        buttonLabel: { en: 'Subscribe', fa: 'عضویت' },
      }

    case 'BlockLeadMagnet':
      return {
        title: { en: 'Free download', fa: 'دانلود رایگان' },
        subtitle: { en: 'Leave your phone to get the file.', fa: 'شماره بگذارید و فایل را بگیرید.' },
        magnetId: '',
      }
    case 'BlockChecklist':
      return {
        title: { en: 'Health checklist', fa: 'چک‌لیست سلامت' },
        subtitle: { en: 'Get the list after entering your phone.', fa: 'با ثبت موبایل چک‌لیست را ببینید.' },
        magnetId: '',
      }
    case 'BlockLeadCalculator':
      return {
        title: { en: 'Health calculator', fa: 'ماشین‌حساب سلامت' },
        subtitle: { en: 'Answer a few questions for a personal result.', fa: 'چند سؤال پاسخ دهید و نتیجه شخصی بگیرید.' },
        calculatorId: '',
      }
    case 'BlockLeadQuiz':
      return {
        title: { en: 'Quick medical quiz', fa: 'آزمون کوتاه پزشکی' },
        subtitle: { en: 'Find out where you stand.', fa: 'وضعیت خود را سریع بسنجید.' },
        quizId: '',
      }
    case 'BlockConsultationForm':
      return {
        title: { en: 'Free consultation', fa: 'مشاوره رایگان' },
        subtitle: { en: 'Describe your concern and we will call you.', fa: 'مشکل را بنویسید تا تماس بگیریم.' },
      }
    case 'BlockLeadSurvey':
      return {
        title: { en: 'Short survey', fa: 'نظرسنجی کوتاه' },
        subtitle: { en: 'A few questions, optional reward.', fa: 'چند سؤال با جایزه اختیاری.' },
        surveyId: '',
      }
    case 'BlockPersonalizedGuide':
      return {
        title: { en: 'Your personal guide', fa: 'راهنمای شخصی شما' },
        subtitle: { en: 'Answers unlock the right content.', fa: 'پاسخ‌ها محتوای مناسب را باز می‌کنند.' },
        guideId: '',
      }
    case 'BlockEventRegistration':
      return {
        title: { en: 'Register for the event', fa: 'ثبت‌نام در رویداد' },
        subtitle: { en: 'Save your seat with your phone number.', fa: 'با موبایل صندلی خود را رزرو کنید.' },
        webinarId: '',
      }
    case 'BlockWaitlistForm':
      return {
        title: { en: 'Join the waitlist', fa: 'عضویت در لیست انتظار' },
        subtitle: { en: 'Be first when the service launches.', fa: 'اولین نفر از شروع خدمت باخبر شوید.' },
        waitlistId: '',
      }
    case 'BlockSmartContactForm':
      return {
        title: { en: 'Contact us', fa: 'تماس با ما' },
        subtitle: { en: 'Tell us what you need.', fa: 'نیاز خود را بنویسید.' },
        formId: '',
      }
    case 'BlockLeadChatWidget':
      return {
        title: { en: 'Ask a question', fa: 'سوال بپرسید' },
        subtitle: { en: 'A short chat before booking.', fa: 'گفت‌وگوی کوتاه قبل از نوبت.' },
      }

    default:
      return {}
  }
}

/** Default page seed when user clicks "Add new page" with a known slug template. */
export function buildPageDefaults(): { blocks: Array<{ component: string; props: Record<string, any>; enabled?: boolean }> } {
  return {
    blocks: [
      {
        component: 'BlockLandingHero',
        enabled: true,
        props: buildBlockDefaults('BlockLandingHero'),
      },
    ],
  }
}

/** True when the block is allowed for the site's effective modules (any-of requiredModules). */
export function isBlockAvailable(
  component: string,
  enabledModuleKeys: Set<string>,
  siteId?: string | null,
): boolean {
  const meta = BLOCK_META[component]
  if (!meta) return true
  if (meta.tenantAllowlist?.length) {
    if (!siteId || !meta.tenantAllowlist.includes(siteId)) return false
  }
  if (!meta.requiredModules?.length) return true
  return meta.requiredModules.some((key) => enabledModuleKeys.has(key))
}
